/** Spellbook logic: mastery, mana cost, filtering, and why a spell is greyed.
 *
 * Pure. The kernel is still the authority - `SpellBook` takes an optional
 * `castReason` prop that the app fills from a real legality probe - but the
 * book has to grey ninety spells sixty times a second while the mouse moves,
 * and asking the kernel for each is neither necessary nor cheap. These
 * functions reproduce the kernel's own tests so the two agree.
 */

import type {
  ContentRegistry, HeroState, Reason, SkillDefId, SpellDef, SpellDefId, SpellSchool,
} from "@game/core";
import { skillLevel } from "@game/core";

/** Wisdom caps the spell level a hero may cast: none 1, basic 2, adv 3, exp 5. */
export const WISDOM_SPELL_LEVEL: readonly number[] = Object.freeze([1, 2, 3, 5]);

/** School -> the secondary skill whose level is that school's mastery. */
export const SCHOOL_SKILL: Readonly<Record<string, SkillDefId>> = Object.freeze({
  air: "air_magic" as SkillDefId, earth: "earth_magic" as SkillDefId,
  fire: "fire_magic" as SkillDefId, water: "water_magic" as SkillDefId,
});

export const SCHOOLS: readonly SpellSchool[] =
  Object.freeze(["air", "earth", "fire", "water", "all"]);

export const SCHOOL_LABEL: Readonly<Record<SpellSchool, string>> = Object.freeze({
  air: "Air", earth: "Earth", fire: "Fire", water: "Water", all: "Neutral",
});

export type SpellBookTab = "combat" | "adventure";

/** 0 none, 1 basic, 2 advanced, 3 expert. Neutral spells have no school skill.
 *  The kernel's `skillLevel` - the raw holding, which is what mastery reads. */
export function masteryFor(hero: HeroState, school: SpellSchool): 0 | 1 | 2 | 3 {
  const skill = SCHOOL_SKILL[school];
  return skill === undefined ? 0 : skillLevel(hero, skill);
}

export const wisdomLevel = (hero: HeroState): number =>
  WISDOM_SPELL_LEVEL[skillLevel(hero, "wisdom" as SkillDefId)] ?? 1;

/**
 * The mana this hero pays for this spell.
 *
 * `SpellDef.cost` is indexed by mastery, so Expert Air Magic makes Chain
 * Lightning cheaper by reading a different column, not by applying a discount.
 * Artifact and skill `spellCost` modifiers are the kernel's business; a host
 * that wants them exact passes `manaCost` into the screen instead.
 */
export function spellManaCost(
  content: ContentRegistry, hero: HeroState, spell: SpellDefId,
): number {
  const def = content.spells.get(spell);
  if (def === undefined) return 0;
  const mastery = masteryFor(hero, def.school);
  return def.cost[mastery] ?? def.cost[0] ?? 0;
}

// ----------------------------------------------------------------- filtering

export interface SpellFilter {
  readonly tab: SpellBookTab;
  /** Null shows every school. */
  readonly school: SpellSchool | null;
}

/**
 * The spells this hero's book shows under the current tab and school.
 *
 * Sorted by level then name so the pages are stable: a book that reorders
 * itself when a spell is learned is a book you cannot learn the shape of.
 */
export function filterSpells(
  content: ContentRegistry, hero: HeroState, filter: SpellFilter,
): readonly SpellDef[] {
  const out: SpellDef[] = [];
  for (const id of hero.spellbook) {
    const def = content.spells.get(id);
    if (def === undefined) continue;
    if (filter.tab === "combat" ? !def.combat : !def.adventure) continue;
    if (filter.school !== null && def.school !== filter.school) continue;
    out.push(def);
  }
  out.sort((a, b) =>
    a.level - b.level
    || (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  return out;
}

/** The schools present in a hero's book under one tab, for the tab strip. */
export function schoolsPresent(
  content: ContentRegistry, hero: HeroState, tab: SpellBookTab,
): readonly SpellSchool[] {
  const seen = new Set<SpellSchool>();
  for (const id of hero.spellbook) {
    const def = content.spells.get(id);
    if (def === undefined) continue;
    if (tab === "combat" ? !def.combat : !def.adventure) continue;
    seen.add(def.school);
  }
  return SCHOOLS.filter((s) => seen.has(s));
}

// ------------------------------------------------------------------ paging

export interface SpellSpread {
  readonly left: readonly SpellDef[];
  readonly right: readonly SpellDef[];
}

/** Deal the filtered spells onto two facing pages, `perPage` cells each. */
export function spellSpread(
  spells: readonly SpellDef[], page: number, perPage: number,
): SpellSpread {
  const per = perPage < 1 ? 1 : perPage;
  const start = page * per * 2;
  return {
    left: spells.slice(start, start + per),
    right: spells.slice(start + per, start + per * 2),
  };
}

export const pageCount = (spells: readonly SpellDef[], perPage: number): number => {
  const per = (perPage < 1 ? 1 : perPage) * 2;
  return Math.max(1, Math.ceil(spells.length / per));
};

// ---------------------------------------------------------------- legality

export interface CastContext {
  /** False on the adventure map, true while a battle is up. */
  readonly inBattle: boolean;
  /** Set when the hero has already cast this round; battle only. */
  readonly castThisRound: boolean;
}

/**
 * Why this spell cannot be cast right now, or null.
 *
 * The order matters and matches the kernel's: no book beats not known beats
 * wrong context beats wisdom beats mana, so the message a player sees is the
 * first thing they would have to fix.
 */
export function castIllegality(
  content: ContentRegistry, hero: HeroState, spell: SpellDefId, ctx: CastContext,
): Reason | null {
  if (hero.spellbook.length === 0) return { code: "NO_SPELLBOOK" };
  if (!hero.spellbook.includes(spell)) return { code: "SPELL_NOT_KNOWN", spell };
  const def = content.spells.get(spell);
  if (def === undefined) return { code: "NO_SUCH_ENTITY", id: spell as string };
  if (ctx.inBattle ? !def.combat : !def.adventure) return { code: "TARGET_INVALID" };
  if (ctx.inBattle && ctx.castThisRound) return { code: "ALREADY_CAST_THIS_ROUND" };
  if (def.level > wisdomLevel(hero)) return { code: "TARGET_INVALID" };
  const cost = spellManaCost(content, hero, spell);
  if (hero.mana < cost) return { code: "OUT_OF_MANA", have: hero.mana, need: cost };
  return null;
}

/** Skill def ids the book needs to name; exported so a test can pin them. */
export const schoolSkillId = (school: SpellSchool): SkillDefId | null =>
  (SCHOOL_SKILL[school] ?? null) as SkillDefId | null;
